import { FC, useContext, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectIsOwner } from 'containers/app/appSlice';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AddIconWoPadding,
  CalendarIcon,
  CustomerNotesIcon,
  DirectionsIcon,
  EditIconBlue,
  MailIcon,
  MapIcon,
  PhoneIcon2
} from 'assets/icons';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials, openInNewTab } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';
import { Job, JobTimelineEvent, JobTimelineEventType } from '../../jobs.model';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import RelatedInvoiceCard from 'containers/invoices/components/RelatedInvoiceCard';
import RelatedEstimateCard from 'containers/estimates/components/RelatedEstimateCard';
import JobTimeline from 'containers/jobs/components/JobTimeline';
import { getAddressAsString } from 'containers/jobs/utils';
import { CustomInput, If } from 'components';
import { AttachFileIcon, SendLineIcon } from 'assets/icons';
import { useUnlinkJobAndInvoiceMutation } from 'containers/jobs/api';
import { GenericDeleteModal } from 'components';
import { useDisconnectJobOrInvoiceFromEstimateMutation } from 'containers/estimates/api';
import { ENTITY_TYPE } from 'constants/common';
import { UnlinkSourceType } from 'containers/estimates/constants';
import { CleverTapEventsInvoices } from 'containers/invoices/events';
import { logAnalyticEvent } from 'utils/analytics';
import { SubscriptionStatus } from 'containers/app/app.model';
import routesPath from 'routes/RoutesPath';
import { resetEstimateState } from 'containers/estimates/estimatesSlice';
import { JobsCleverTapEvents } from 'containers/jobs/events';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import { RelatedInvoiceForJob } from 'containers/jobs/jobs.model';
import { JobPermissionsContext } from '../JobDetails';
import { GOOGLE_MAPS_BASE_URL } from 'config/index';
import { JobDetailsTabs } from '../constants';

interface RightSectionJobDetailProps {
  job: Job;
  jobTimeline: JobTimelineEvent[];
  onEditScheduleClick: () => void;
  onEditAssigneesClick: () => void;
  onEditNoteClick: (event: JobTimelineEvent) => void;
  onDeleteNoteClick: (event: JobTimelineEvent) => void;
  onAddNoteClick: () => void;
  getJobTimeline: () => void;
  refetchJobDetails: () => void;
  onCreateNote: (note: string, clearInput: () => void) => void;
  onInvoiceClick: (invoiceId: string) => void;
  onEstimateClick: (estimateId: string) => void;
  onCustomerClick: () => void;
  currentJobTab: JobDetailsTabs;
}

const RightSectionJobDetail: FC<RightSectionJobDetailProps> = ({
  job,
  jobTimeline,
  onEditScheduleClick,
  onEditAssigneesClick,
  onEditNoteClick,
  onDeleteNoteClick,
  onAddNoteClick,
  refetchJobDetails,
  onCreateNote,
  onInvoiceClick,
  onEstimateClick,
  onCustomerClick,
  currentJobTab
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);
  const [currentNotesActionsPopoverId, setCurrentNotesActionsPopoverId] = useState(null);
  const [notesInput, setNotesInput] = useState('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState({ id: '', isOpen: false, type: null });
  const [unlinkJobAndInvoiceMutation, { isLoading: isUnlinkInProgress }] = useUnlinkJobAndInvoiceMutation();
  const [disconnectJobOrInvoiceFromEstimate] = useDisconnectJobOrInvoiceFromEstimateMutation();
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const { rootJobId, rootCustomerId, rootDisputeId, rootReceiptId, rootInvoiceId, rootEstimateId } = useParams();
  const { canEditJobs } = useContext(JobPermissionsContext);

  // Filter notes from timeline
  const notes = jobTimeline?.filter(event => event.type === JobTimelineEventType.Note);

  const formatScheduleTime = (date: string) => {
    if (!date) return '';
    return DateUtils.getDateInFormat({
      date: new Date(date),
      dateFormat: 'h:mm a'
    });
  };

  const formatScheduleDate = (date: string) => {
    if (!date) return '';
    return DateUtils.getDateInFormat({
      date: new Date(date),
      dateFormat: 'MMM dd, yyyy'
    });
  };

  const handleToggleMap = e => {
    e.stopPropagation();
    setIsMapExpanded(!isMapExpanded);
  };

  const handleShareInvoice = (invoice: RelatedInvoiceForJob) => {
    if (invoice.status === InvoiceStatus.PENDING) {
      if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else {
      if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    }
  };

  const handleRemoveInvoice = (invoiceId: string) => {
    setIsConfirmationModalOpen({ id: invoiceId, isOpen: true, type: ENTITY_TYPE.INVOICE });
  };

  const handleRemoveEstimate = (estimateId: string) => {
    setIsConfirmationModalOpen({ id: estimateId, isOpen: true, type: ENTITY_TYPE.ESTIMATE });
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen({ id: '', isOpen: false, type: null });
  };

  const handleRemoveInvoiceFromJob = () => {
    unlinkJobAndInvoiceMutation({
      jobId: job.id,
      invoiceId: isConfirmationModalOpen.id,
      teamId: currentTeam.id,
      sourceType: UnlinkSourceType.JOB
    });
    handleCloseConfirmationModal();
    refetchJobDetails();
  };

  const handleRemoveEstimateFromJob = async () => {
    try {
      await disconnectJobOrInvoiceFromEstimate({
        teamId: currentTeam.id,
        estimateId: isConfirmationModalOpen.id,
        entityType: ENTITY_TYPE.JOB,
        entityID: job.id,
        sourceType: UnlinkSourceType.JOB
      }).unwrap();
      handleCloseConfirmationModal();
      refetchJobDetails();
    } catch (error) {
      // Handle error
    }
  };

  const onSendNoteClick = () => {
    onCreateNote(notesInput, () => setNotesInput(''));
  };

  const handleAnalyticEventForNotes = () => {
    logAnalyticEvent(JobsCleverTapEvents.webEnterNotesTextBoxClicked);
  };

  const handleAddInvoiceFromJob = () => {
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoice, { isFromJob: true });
    navigate(`/invoices/create?jobId=${job.id}`, {
      state: {
        returnTo: location.pathname,
        returnToState: {
          selectedJobTab: currentJobTab
        }
      }
    });
  };

  const handleAddEstimateFromJob = () => {
    // logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateEstimate, { isFromJob: true });
    if (
      currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED ||
      currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED
    ) {
      if (isOwner) navigate(routesPath.ESTIMATE_INTEGRATION);
      else navigate(routesPath.ESTIMATES);
      return;
    }
    dispatch(resetEstimateState());
    navigate(`/estimates/create?jobId=${job.id}`, {
      state: {
        returnTo: location.pathname,
        returnToState: {
          selectedJobTab: currentJobTab
        }
      }
    });
  };

  const showAddEstimateButton =
    (!job?.invoices || job.invoices.length === 0) && (!job?.estimates || job.estimates.length === 0) && canEditJobs;

  const showEmptyLinkedDocuments =
    (!job?.invoices || job.invoices.length === 0) && (!job?.estimates || job.estimates.length === 0) && !canEditJobs;

  // eslint-disable-next-line react/no-multi-comp
  const NotesInputSuffix = () => (
    <div className="flex items-center gap-x-4">
      <AttachFileIcon className="shrink-0 cursor-pointer" onClick={onAddNoteClick} />
      <SendLineIcon className="shrink-0 cursor-pointer" width={18} height={18} onClick={onSendNoteClick} />
    </div>
  );

  return (
    <>
      <div className="flex h-full flex-col gap-2 sm:w-[360px]">
        {/* Job Schedule Card */}
        <div className="rounded-md border border-borderGray bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-17px font-semibold text-primaryText">Job schedule</div>
            <If condition={job?.isScheduled && canEditJobs}>
              <EditIconBlue className="cursor-pointer" onClick={onEditScheduleClick} height={16} width={16} />
            </If>
          </div>
          {job?.isScheduled && job?.scheduledFrom && job?.scheduledTo ? (
            <div className="mt-3 flex items-center gap-2">
              <CalendarIcon className="path path-fill-current shrink-0 text-primaryText" />
              <div>
                <div className="text-sbase font-medium text-primaryText">
                  {formatScheduleDate(job.scheduledFrom)}, {formatScheduleTime(job.scheduledFrom)} -{' '}
                  {formatScheduleTime(job.scheduledTo)}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sbase text-accentText">No schedule added</div>
              {canEditJobs && (
                <div
                  className="flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                    border-primaryBtn p-3 pr-4"
                  role="presentation"
                  onClick={onEditScheduleClick}>
                  <div className="flex items-center">
                    <AddIconWoPadding className="mr-2 shrink-0" />
                    <div className="text-sbase font-semibold text-primaryBtn">Add schedule</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Job For Card */}
        <div className="rounded-md border border-borderGray bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-17px font-semibold text-primaryText">Job for</div>
            <div className="cursor-pointer text-sbase font-semibold text-secondary" onClick={onCustomerClick}>
              View customer details
            </div>
          </div>
          {job?.customerCurrent && (
            <>
              <div className="mt-3 flex items-center gap-3">
                <CustomAvatar
                  src={''}
                  className="text-base text-primaryText"
                  label={getInitials(job.customerCurrent.name)}>
                  {getInitials(job.customerCurrent.name)}
                </CustomAvatar>
                <div className="grow">
                  <div className="text-sbase font-semibold text-primaryText">
                    {job.customerCurrent.name} {job.customerCurrent.lastName}
                  </div>
                  <div className="text-px13 text-accentText">{job.customerCurrent.companyName}</div>
                </div>
                <div className="flex items-center gap-7">
                  <a
                    href={`tel:${job.customerCurrent?.phoneNumber}`}
                    onClick={e => {
                      if (!job.customerCurrent?.phoneNumber) e.preventDefault();
                    }}>
                    <PhoneIcon2 className="path-fill-current h-4 w-4 cursor-pointer text-secondary" />
                  </a>
                  <a
                    href={`mailto:${job.customerCurrent?.email}`}
                    onClick={e => {
                      if (!job.customerCurrent?.email) e.preventDefault();
                    }}>
                    <MailIcon className="h-4 w-4 cursor-pointer text-secondary" />
                  </a>
                </div>
              </div>
              <div className="my-4 border-t border-borderGray"></div>
            </>
          )}
          {job?.customerCurrent?.serviceAddress?.[0] && (
            <div>
              <div className="flex items-center gap-2 text-px13 font-medium text-primaryText">
                <MapIcon className="h-4 w-4 shrink-0" />
                <span>Service address</span>
              </div>
              <div className="flex flex-col">
                <div className="mt-2 flex cursor-pointer items-center justify-between" onClick={handleToggleMap}>
                  <div className="pr-2 text-sbase text-primaryText">
                    {job.customerCurrent.serviceAddress[0]?.addressName ||
                      getAddressAsString(job.customerCurrent.serviceAddress[0])}
                  </div>
                  {/* <DropDownChevronDownIcon
                    className={`shrink-0 text-accentText transition-transform duration-200 ${
                      isMapExpanded ? 'rotate-180' : ''
                    }`}
                  /> */}
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      openInNewTab(
                        `${GOOGLE_MAPS_BASE_URL}${getAddressAsString(job.customerCurrent.serviceAddress[0])}`
                      );
                    }}>
                    <DirectionsIcon />
                  </div>
                </div>

                {/* Map expansion with transition */}
                {/* <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isMapExpanded ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                  <div className="mt-3 h-[330px] w-full overflow-hidden rounded-md border border-borderGray">
                    <MapsWithMarkers
                      showSingleMarker
                      address={job.customerCurrent.serviceAddress[0]}
                      isMinimalMap
                      isLoading={false}
                    />
                  </div>

                  {job.customerCurrent.serviceAddress[0].addressNote && (
                    <div className="mt-2">
                      <div className="ml-[-6px] flex items-center gap-1.5">
                        <CustomerNotesIcon className="path-fill-current path h-6 w-6 text-accentText" />
                        <div className="text-sbase font-semibold text-primaryText">Address Note</div>
                      </div>
                      <div className="mt-2 text-sbase text-accentText">
                        {job.customerCurrent.serviceAddress[0].addressNote}
                      </div>
                    </div>
                  )}
                </div> */}
              </div>
            </div>
          )}
        </div>

        {/* Notes Card */}
        <div className="rounded-md border border-borderGray bg-white p-4">
          <div className="flex items-center">
            <div className="text-17px font-semibold text-primaryText">Notes</div>
          </div>
          <div className="mt-3 flex max-h-[286px] flex-col gap-4">
            {notes && notes.length > 0 ? (
              <JobTimeline
                jobTimeline={notes}
                onEditNoteClick={onEditNoteClick}
                onDeleteNoteClick={onDeleteNoteClick}
                hasImageViewer={false}
                timelineId="job-details-notes-timeline"
                currentActionsPopoverId={currentNotesActionsPopoverId}
                setCurrentActionsPopoverId={setCurrentNotesActionsPopoverId}
                timelineClassName="pb-8"
              />
            ) : (
              <div className="flex h-[152px] flex-col items-center justify-center gap-2">
                <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-gray-1/20">
                  <CustomerNotesIcon className="path path-fill-current h-8 w-8 text-primaryText" />
                </div>
                <div className="text-sbase text-accentText">No note created yet</div>
              </div>
            )}
            <div className="mt-2">
              <CustomInput
                id="job-details-notes-input"
                value={notesInput}
                onChange={setNotesInput}
                hasSuffix={true}
                showSuffixAlways={true}
                suffix={<NotesInputSuffix />}
                className="h-12 rounded-md border border-borderGray bg-transparent pr-[72px]
                        placeholder:font-semibold focus:outline-none"
                suffixStyle="right-4 top-4"
                placeholder="Enter note"
                onClick={handleAnalyticEventForNotes}
              />
            </div>
          </div>
        </div>

        {/* Assigned Team Card */}
        <div className="rounded-md border border-borderGray bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-17px font-semibold text-primaryText">Assigned team</div>
            <If condition={job?.assignees && job.assignees.length > 0 && canEditJobs}>
              <EditIconBlue className="cursor-pointer" onClick={onEditAssigneesClick} height={16} width={16} />
            </If>
          </div>
          <div className="mt-3 flex flex-col gap-3 divide-y">
            {job?.assignees && job.assignees.length > 0 ? (
              job.assignees.map((assignee, index) => (
                <div key={`assignee-${index}`} className="flex items-center gap-3 pt-2">
                  <CustomAvatar
                    src={assignee.profilePictureURL}
                    height="h-6"
                    width="w-6"
                    className="text-[9px] font-semibold text-primaryText"
                    label={getInitials(assignee.name)}>
                    {getInitials(assignee.name)}
                  </CustomAvatar>
                  <div className="text-sbase text-primaryText">{assignee.name}</div>
                </div>
              ))
            ) : (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sbase text-accentText">No members assigned </div>
                {canEditJobs && (
                  <div
                    className="flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                    border-primaryBtn p-3 pr-4"
                    role="presentation"
                    onClick={onEditAssigneesClick}>
                    <div className="flex items-center">
                      <AddIconWoPadding className="mr-2 shrink-0" />
                      <div className="text-sbase font-semibold text-primaryBtn">Add members</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Linked Documents Card */}
        <div className="rounded-md border border-borderGray bg-white p-6 pb-24">
          <div className="flex items-center justify-between">
            <div className="text-17px font-semibold text-primaryText">Linked documents</div>
          </div>

          <div className="mt-4 flex flex-col gap-1">
            {job?.invoices && job.invoices.length > 0 ? (
              <div className="flex flex-col">
                <div className="text-sbase font-medium text-primaryText">Invoice</div>
                {job.invoices.map((invoice, index) => (
                  <RelatedInvoiceCard
                    key={`invoice-${index}`}
                    invoice={invoice}
                    onInvoiceClick={onInvoiceClick}
                    onRemoveInvoice={handleRemoveInvoice}
                    onShareInvoice={handleShareInvoice}
                    className="rounded-none border-0 px-0 pb-4"
                  />
                ))}
              </div>
            ) : (
              canEditJobs && (
                <div
                  className="flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
                  role="presentation"
                  onClick={handleAddInvoiceFromJob}>
                  <div className="flex items-center">
                    <AddIconWoPadding className="mr-2 shrink-0" />
                    <div className="text-sbase font-semibold text-primaryBtn">Add invoice</div>
                  </div>
                </div>
              )
            )}

            {job?.estimates && job.estimates.length > 0 && (
              <div className="flex flex-col border-t border-borderGray pt-4">
                <div className="text-sbase font-medium text-primaryText">Estimate</div>
                {job.estimates.map((estimate, index) => (
                  <RelatedEstimateCard
                    key={`estimate-${index}`}
                    estimate={estimate}
                    onEstimateClick={onEstimateClick}
                    onRemoveEstimate={handleRemoveEstimate}
                    className="rounded-none border-0 px-0"
                  />
                ))}
              </div>
            )}

            {showAddEstimateButton && (
              <div
                className="mt-4 flex cursor-pointer items-center justify-center gap-x-3 rounded-md border 
                    border-borderGray p-3 pr-4 hover:bg-secondaryBtn"
                role="presentation"
                onClick={handleAddEstimateFromJob}>
                <div className="flex items-center">
                  <AddIconWoPadding className="mr-2 shrink-0" />
                  <div className="text-sbase font-semibold text-primaryBtn">Add estimate</div>
                </div>
              </div>
            )}
            {showEmptyLinkedDocuments && (
              <div className="mt-5 flex flex-col items-center justify-center gap-1">
                <div className="text-sbase text-accentText">No linked documents</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {isConfirmationModalOpen.isOpen && isConfirmationModalOpen.type === ENTITY_TYPE.INVOICE && (
        <GenericDeleteModal
          onPrimaryBtnClick={handleRemoveInvoiceFromJob}
          onSecondaryBtnClick={handleCloseConfirmationModal}
          isActionInProgress={isUnlinkInProgress}
          primaryBtnId="remove-invoice-confirm"
          primaryBtnLabel="Yes, remove"
          secondaryBtnId="remove-invoice-cancel"
          secondaryBtnLabel="No, cancel"
          showModal={true}
          title="Remove invoice"
          subTitle="Are you sure you want to remove invoice from job?"
        />
      )}

      {isConfirmationModalOpen.isOpen && isConfirmationModalOpen.type === ENTITY_TYPE.ESTIMATE && (
        <GenericDeleteModal
          onPrimaryBtnClick={handleRemoveEstimateFromJob}
          onSecondaryBtnClick={handleCloseConfirmationModal}
          isActionInProgress={false}
          primaryBtnId="remove-estimate-confirm"
          primaryBtnLabel="Yes, remove"
          secondaryBtnId="remove-estimate-cancel"
          secondaryBtnLabel="No, cancel"
          showModal={true}
          title="Remove estimate"
          subTitle="Are you sure you want to remove estimate from job?"
        />
      )}
    </>
  );
};

export default RightSectionJobDetail;
